/**
 * Turns `GET /api/stats/period` (one row per campaign and per automation
 * email) into the three report levels of the Statistics page, plus the
 * change against a compare period. Pure: no React, no fetching.
 */
import type { PeriodStatsItem, PeriodStatsResponse } from '../../lib/api';
import type { StatsLevel } from './statsParams';

export interface Metrics {
  sent: number;
  delivered: number;
  opened: number;
  botOpened: number;
  clicked: number;
  unsubscribed: number;
  bounced: number;
  complained: number;
  orders: number;
  revenue: number;
}

const METRIC_FIELDS: (keyof Metrics)[] = [
  'sent',
  'delivered',
  'opened',
  'botOpened',
  'clicked',
  'unsubscribed',
  'bounced',
  'complained',
  'orders',
  'revenue',
];

export function zeroMetrics(): Metrics {
  return {
    sent: 0,
    delivered: 0,
    opened: 0,
    botOpened: 0,
    clicked: 0,
    unsubscribed: 0,
    bounced: 0,
    complained: 0,
    orders: 0,
    revenue: 0,
  };
}

export function sumMetrics(list: Partial<Metrics>[]): Metrics {
  const out = zeroMetrics();
  for (const m of list) for (const k of METRIC_FIELDS) out[k] += m[k] ?? 0;
  return out;
}

function averageMetrics(list: Metrics[]): Metrics {
  const total = sumMetrics(list);
  if (list.length === 0) return total;
  for (const k of METRIC_FIELDS) total[k] = total[k] / list.length;
  return total;
}

export type MetricKey =
  | 'sent'
  | 'openRate'
  | 'botOpened'
  | 'clickRate'
  | 'orders'
  | 'revenue'
  | 'unsubRate'
  | 'bounceRate';

export interface MetricDef {
  key: MetricKey;
  label: string;
  format: 'number' | 'percent' | 'money';
  /** Rates change in percentage points, counts in %. */
  isRate: boolean;
  /** Which direction is good news; `neutral` is shown grey. */
  good: 'up' | 'down' | 'neutral';
}

export const METRICS: Record<MetricKey, MetricDef> = {
  sent: { key: 'sent', label: 'Sent', format: 'number', isRate: false, good: 'up' },
  openRate: { key: 'openRate', label: 'Open rate', format: 'percent', isRate: true, good: 'up' },
  botOpened: {
    key: 'botOpened',
    label: 'Bot opens',
    format: 'number',
    isRate: false,
    good: 'neutral',
  },
  clickRate: { key: 'clickRate', label: 'Click rate', format: 'percent', isRate: true, good: 'up' },
  orders: { key: 'orders', label: 'Orders', format: 'number', isRate: false, good: 'up' },
  revenue: { key: 'revenue', label: 'Revenue', format: 'money', isRate: false, good: 'up' },
  unsubRate: {
    key: 'unsubRate',
    label: 'Unsub rate',
    format: 'percent',
    isRate: true,
    good: 'down',
  },
  bounceRate: {
    key: 'bounceRate',
    label: 'Bounce rate',
    format: 'percent',
    isRate: true,
    good: 'down',
  },
};

/** Columns of the report table, in order. */
export const TABLE_METRICS: MetricKey[] = [
  'sent',
  'openRate',
  'botOpened',
  'clickRate',
  'orders',
  'revenue',
  'unsubRate',
  'bounceRate',
];

/** The denominator a rate is taken over (0 = no rate). */
function denominator(m: Metrics, key: MetricKey): number {
  return key === 'bounceRate' ? m.sent : m.delivered;
}

export function metricValue(m: Metrics, key: MetricKey): number {
  switch (key) {
    case 'openRate':
      return m.delivered > 0 ? m.opened / m.delivered : 0;
    case 'clickRate':
      return m.delivered > 0 ? m.clicked / m.delivered : 0;
    case 'unsubRate':
      return m.delivered > 0 ? m.unsubscribed / m.delivered : 0;
    case 'bounceRate':
      return m.sent > 0 ? m.bounced / m.sent : 0;
    default:
      return m[key];
  }
}

export interface Delta {
  /** `good`/`bad` colour by whether the change is welcome; `flat` for "± 0", "—" and "new". */
  tone: 'good' | 'bad' | 'flat' | 'neutral';
  text: string;
}

const NOTHING: Delta = { tone: 'flat', text: '—' };
const SAME: Delta = { tone: 'flat', text: '± 0' };

/** Change of one metric against the compare period ("▲ 1.2 pts", "▼ 8.0%", "± 0", "—", "new"). */
export function metricDelta(key: MetricKey, cur: Metrics, prev: Metrics): Delta {
  const def = METRICS[key];
  const a = metricValue(cur, key);
  const b = metricValue(prev, key);
  let diff: number;
  let text: string;
  if (def.isRate) {
    const curHas = denominator(cur, key) > 0;
    const prevHas = denominator(prev, key) > 0;
    if (!curHas && !prevHas) return SAME;
    if (!curHas || !prevHas) return NOTHING;
    diff = (a - b) * 100;
    text = `${Math.abs(diff).toFixed(1)} pts`;
  } else {
    if (a === 0 && b === 0) return SAME;
    if (prev.sent === 0 && key !== 'sent') return NOTHING;
    if (b === 0) return { tone: 'flat', text: 'new' };
    diff = ((a - b) / b) * 100;
    text = `${Math.abs(diff).toFixed(1)}%`;
  }
  if (Math.abs(diff) < 0.05) return SAME;
  const arrow = diff > 0 ? '▲' : '▼';
  const tone: Delta['tone'] =
    def.good === 'neutral' ? 'neutral' : diff > 0 === (def.good === 'up') ? 'good' : 'bad';
  return { tone, text: `${arrow} ${text}` };
}

export type RowAction =
  | { kind: 'level'; level: 'campaigns' }
  | { kind: 'flow'; automationId: string }
  | { kind: 'campaign'; campaignId: string };

export interface ReportRow {
  id: string;
  name: string;
  type: 'Campaigns' | 'Campaign' | 'Automation' | 'Email';
  action: RowAction | null;
  /** Sent date for a campaign. */
  sentAt: number | null;
  cur: Metrics;
  /** `null` when compare is off. */
  prev: Metrics | null;
}

export interface ReportView {
  rows: ReportRow[];
  total: Metrics;
  totalPrev: Metrics | null;
  /** The automation drilled into, if any. */
  flow: { id: string; name: string } | null;
  /** Some sender in view does not track opens, so the open rate misses those mails. */
  opensIncomplete: boolean;
}

const pickMetrics = (i: Partial<Metrics>): Metrics => sumMetrics([i]);

const autoIdOf = (i: PeriodStatsItem) => i.automationId ?? 'unknown';

interface AutomationGroup {
  id: string;
  name: string;
  steps: PeriodStatsItem[];
  extra: Metrics;
}

function groupAutomations(data: PeriodStatsResponse): Map<string, AutomationGroup> {
  const groups = new Map<string, AutomationGroup>();
  const get = (id: string, name: string | null) => {
    let g = groups.get(id);
    if (!g) {
      g = { id, name: name ?? 'Automation', steps: [], extra: zeroMetrics() };
      groups.set(id, g);
    } else if (name && g.name === 'Automation') g.name = name;
    return g;
  };
  for (const item of data.items) {
    if (item.kind === 'flow_step') get(autoIdOf(item), item.automationName).steps.push(item);
  }
  for (const o of data.automationOrders) {
    const g = get(o.automationId, null);
    g.extra = sumMetrics([g.extra, { orders: o.orders, revenue: o.revenue }]);
  }
  return groups;
}

function automationTotal(g: AutomationGroup | undefined): Metrics {
  return g ? sumMetrics([...g.steps.map(pickMetrics), g.extra]) : zeroMetrics();
}

const campaignsOf = (data: PeriodStatsResponse) => data.items.filter((i) => i.kind === 'campaign');

function stepId(i: PeriodStatsItem): string {
  return i.stepId ?? i.key;
}

/**
 * Builds the rows and totals for one level. Campaigns are compared with the
 * average campaign of the compare period (they are different mails);
 * automations and their emails are compared with themselves.
 */
export function buildReport(
  cur: PeriodStatsResponse,
  prev: PeriodStatsResponse | null,
  level: StatsLevel,
  flowId: string | null,
): ReportView {
  const curAutos = groupAutomations(cur);
  const prevAutos = prev ? groupAutomations(prev) : null;
  const curCampaigns = campaignsOf(cur);
  const prevCampaigns = prev ? campaignsOf(prev) : [];

  const campaignsTotal = sumMetrics(curCampaigns.map(pickMetrics));
  const campaignsTotalPrev = prev ? sumMetrics(prevCampaigns.map(pickMetrics)) : null;
  const avgPrevCampaign = prev ? averageMetrics(prevCampaigns.map(pickMetrics)) : null;

  const autoIds = [...new Set([...curAutos.keys(), ...(prevAutos?.keys() ?? [])])];
  const autoName = (id: string) =>
    curAutos.get(id)?.name ?? prevAutos?.get(id)?.name ?? 'Automation';

  const campaignRows: ReportRow[] = curCampaigns.map((c) => ({
    id: `c:${c.key}`,
    name: c.name,
    type: 'Campaign',
    action: { kind: 'campaign', campaignId: c.key },
    sentAt: c.sentAt,
    cur: pickMetrics(c),
    prev: avgPrevCampaign,
  }));

  const automationRows: ReportRow[] = autoIds.map((id) => ({
    id: `a:${id}`,
    name: autoName(id),
    type: 'Automation',
    action: { kind: 'flow', automationId: id },
    sentAt: null,
    cur: automationTotal(curAutos.get(id)),
    prev: prevAutos ? automationTotal(prevAutos.get(id)) : null,
  }));

  const autosTotal = sumMetrics(automationRows.map((r) => r.cur));
  const autosTotalPrev = prevAutos
    ? sumMetrics(autoIds.map((id) => automationTotal(prevAutos.get(id))))
    : null;

  const untracked = (items: PeriodStatsItem[]) => items.some((i) => i.sent > 0 && !i.opensTracked);

  if (level === 'automations' && flowId) {
    const curGroup = curAutos.get(flowId);
    const prevGroup = prevAutos?.get(flowId);
    const steps = new Map<string, { cur?: PeriodStatsItem; prev?: PeriodStatsItem }>();
    for (const s of curGroup?.steps ?? []) steps.set(stepId(s), { cur: s });
    for (const s of prevGroup?.steps ?? []) {
      const e = steps.get(stepId(s)) ?? {};
      e.prev = s;
      steps.set(stepId(s), e);
    }
    const ordered = [...steps.entries()].sort(
      ([, x], [, y]) =>
        (x.cur?.stepOrder ?? x.prev?.stepOrder ?? 0) - (y.cur?.stepOrder ?? y.prev?.stepOrder ?? 0),
    );
    const rows: ReportRow[] = ordered.map(([id, e], i) => {
      const item = (e.cur ?? e.prev) as PeriodStatsItem;
      return {
        id: `s:${id}`,
        name: `Email ${item.stepOrder ?? i + 1} · ${item.name}`,
        type: 'Email',
        action: null,
        sentAt: null,
        cur: e.cur ? pickMetrics(e.cur) : zeroMetrics(),
        prev: prev ? (e.prev ? pickMetrics(e.prev) : zeroMetrics()) : null,
      };
    });
    return {
      rows,
      total: automationTotal(curGroup),
      totalPrev: prevAutos ? automationTotal(prevGroup) : null,
      flow: { id: flowId, name: autoName(flowId) },
      opensIncomplete: untracked(curGroup?.steps ?? []),
    };
  }

  if (level === 'campaigns') {
    return {
      rows: campaignRows,
      total: campaignsTotal,
      totalPrev: campaignsTotalPrev,
      flow: null,
      opensIncomplete: untracked(curCampaigns),
    };
  }

  if (level === 'automations') {
    return {
      rows: automationRows,
      total: autosTotal,
      totalPrev: autosTotalPrev,
      flow: null,
      opensIncomplete: untracked(cur.items.filter((i) => i.kind === 'flow_step')),
    };
  }

  const rows: ReportRow[] = [];
  if (curCampaigns.length > 0 || prevCampaigns.length > 0) {
    rows.push({
      id: 'campaigns',
      name: 'Campaigns',
      type: 'Campaigns',
      action: { kind: 'level', level: 'campaigns' },
      sentAt: null,
      cur: campaignsTotal,
      prev: campaignsTotalPrev,
    });
  }
  rows.push(...automationRows);
  return {
    rows,
    total: sumMetrics([campaignsTotal, autosTotal]),
    totalPrev:
      campaignsTotalPrev && autosTotalPrev
        ? sumMetrics([campaignsTotalPrev, autosTotalPrev])
        : null,
    flow: null,
    opensIncomplete: untracked(cur.items),
  };
}

export type SortKey = 'name' | MetricKey;
export interface SortState {
  key: SortKey;
  dir: 'asc' | 'desc';
}

export function sortRows(rows: ReportRow[], sort: SortState): ReportRow[] {
  const sign = sort.dir === 'asc' ? 1 : -1;
  return [...rows].sort((x, y) => {
    if (sort.key === 'name') return x.name.localeCompare(y.name, 'en') * sign;
    return (metricValue(x.cur, sort.key) - metricValue(y.cur, sort.key)) * sign;
  });
}

/** Next sort after a header click: same column flips, a new one starts descending (name ascending). */
export function nextSort(current: SortState, key: SortKey): SortState {
  if (current.key === key) return { key, dir: current.dir === 'asc' ? 'desc' : 'asc' };
  return { key, dir: key === 'name' ? 'asc' : 'desc' };
}
